import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import CapsuleTab, { Tab } from '@repo/ui/capsule-tab'
import {
  CalendarTwoIcon,
  ChevronLeftIcon,
  InformationIcon,
  MessageIcon,
  PlusIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import WarningBadge from '@repo/ui/warning-badge'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { createCenterEvent } from '../../../api/center-event/create-center-event'
import { createEnrollmentComment } from '../../../api/enrollments/create-enrollment-comment'
import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { getEnrollmentProfileMissingDocuments } from '../../../api/enrollments/get-enrollment-missing-documents'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import CenterEventForm from '../../../components/pages/center-events/center-event-form'
import EnrollmentProfileCenterInformation from '../../../components/pages/enrollment-profile/enrollment-profile-center-information'
import EnrollmentProfileDocuments from '../../../components/pages/enrollment-profile/enrollment-profile-documents'
import EnrollmentProfileEndOfStayAppreciations from '../../../components/pages/enrollment-profile/enrollment-profile-end-of-stay-appreciations'
import EnrollmentProfileEvents from '../../../components/pages/enrollment-profile/enrollment-profile-events'
import EnrollmentProfileGlobalView from '../../../components/pages/enrollment-profile/enrollment-profile-global-view'
import EnrollmentProfileHeader from '../../../components/pages/enrollment-profile/enrollment-profile-header'
import EnrollmentProfileMonitoring from '../../../components/pages/enrollment-profile/enrollment-profile-monitoring'
import EnrollmentOverpaymentRefundModal from '../../../components/pages/enrollment-profile/shared/enrollment-overpayment-refund-modal'
import EnrollmentAdvisorsSelection from '../../../components/pages/enrollments/enrollment-form/enrollment-advisors-selection'
import EnrollmentCommentForm from '../../../components/pages/enrollments/enrollment-form/enrollment-comment-form'
import EnrollmentComments from '../../../components/pages/enrollments/enrollment-form/enrollment-comments'
import { EnrollmentCseHeader } from '../../../components/pages/enrollments/enrollment-form/enrollment-cse-header'
import EnrollmentDuplicateButton from '../../../components/pages/enrollments/enrollment-form/enrollment-duplicate-button'
import EnrollmentParentsRelatedTripsButton from '../../../components/pages/enrollments/enrollment-form/enrollment-parents-related-trips-button'
import EnrollmentStatusOverview from '../../../components/pages/enrollments/enrollment-form/enrollment-status-overview'
import EnrollmentTripHistoryButton from '../../../components/pages/enrollments/enrollment-form/enrollment-trip-history-button'
import { Paths } from '../../../lib/constants/path'
import EnrollmentProvider from '../../../lib/contexts/enrollment'
import { useObserver } from '../../../lib/contexts/observer'
import useAuth from '../../../lib/hooks/use-auth'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { useSections } from '../../../lib/hooks/use-sections'
import {
  ApplicationStatusEnum,
  applicationStatusEnumMapping
} from '../../../lib/types/enums/enrollment/application-status.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  enrollmentProfileContextFields,
  enrollmentProfileContextJourneyFields,
  enrollmentProfileContextJourneyInternalFields,
  enrollmentProfileContextMainFields
} from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { missingDocumentsMapping } from '../utils'

type Key =
  | 'global-view'
  | 'center-information'
  | 'monitoring'
  | 'documents'
  | 'events'
  | 'end-of-stay-appreciations'

export type AccordionDetail = {
  component: React.ReactNode
  subtitle?: React.ReactNode
}

const tabs: Tab<'required-key', Key>[] = [
  { label: 'Infos globales', key: 'global-view' },
  { label: `Infos centre et voyage`, key: 'center-information' },
  { label: 'Suivi administratif et comptable', key: 'monitoring', isCollapsed: true },
  { label: 'Infos à compléter', key: 'documents', isCollapsed: true },
  { label: 'Evènements', icon: InformationIcon, key: 'events', isCollapsed: true },
  { label: 'Appréciation sur son séjour', key: 'end-of-stay-appreciations' }
]

const EnrollmentProfile = () => {
  const { id } = useParams<{ id: string }>()
  const { back } = useNavigator()
  const { modalState, toggleModal, closeModel } = useModalManager<
    'create-comment' | 'create-event'
  >(['create-comment', 'create-event'])
  const [accordionState, setAccordionState] = useState<string[]>(
    tabs.filter((tab) => !tab.isCollapsed).map((tab) => tab.key)
  )

  const { notify } = useObserver()
  const { userRole } = useAuth()

  // the catalogue prices behind a One to One Individuel proposition are served to LEC and to the
  // prestataire only: the parents asking for them would be refused by the backend
  const journeyFields =
    userRole === RoleEnum.CLIENT
      ? enrollmentProfileContextJourneyFields
      : enrollmentProfileContextJourneyInternalFields

  const { data, isPending, refetch } = useQuery({
    queryKey: ['enrollments', id, enrollmentProfileContextFields, journeyFields],
    queryFn: async () => {
      if (id) {
        const [mainDetails, journeyDetails] = await Promise.all([
          getEnrollment(+id, enrollmentProfileContextMainFields),
          getEnrollment(+id, journeyFields)
        ])

        return { ...mainDetails, ...journeyDetails }
      }
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!id
  })

  useObserver({
    key: 'enrollment-profile',
    update: () => refetch()
  })

  // Filter tabs according to whether a center is assigned so the
  // CapsuleTab and the Accordion list use the exact same set.
  const availableTabs = tabs.filter((tab) => {
    if (tab.key === 'center-information' || tab.key === 'events') return !!data?.enrollmentCenter
    // the end-of-stay ratings are LEC back office only, never the parent space
    if (tab.key === 'end-of-stay-appreciations') return userRole === RoleEnum.ADMIN
    return true
  })
  const { active, handleChange } = useSections({ sections: availableTabs })

  const enrollmentMissingDocuments = useQuery({
    queryKey: ['enrollment-profile-missing-documents', id],
    queryFn: () => {
      if (id) return getEnrollmentProfileMissingDocuments(+id)
    },
    enabled: !!id
  })

  const { mutateAsync: createEnrollmentCommentMutate } = useMutation({
    mutationFn: createEnrollmentComment,
    onError: showAxiosError,
    onSuccess: () => {
      notify(['enrollment-comments'])
      closeModel('create-comment')
      toast.success('Commentaire créé avec succès')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: createCenterEvent,
    onError: showAxiosError,
    onSuccess: () => {
      notify(['enrollment-events'])
      closeModel('create-event')
      toast.success('Évènement créé avec succès')
    }
  })

  const renderAccordion = (key: Key): AccordionDetail => {
    if (!data) return { component: <LoadingScreen /> }
    switch (key) {
      case 'global-view':
        return { component: <EnrollmentProfileGlobalView showEmptyMedicalInformation /> }
      case 'center-information':
        return {
          component: <EnrollmentProfileCenterInformation />,
          subtitle: (
            <Badge variant="success" text={applicationStatusEnumMapping[data.status].label} />
          )
        }
      case 'monitoring':
        return { component: <EnrollmentProfileMonitoring /> }
      case 'documents':
        return { component: <EnrollmentProfileDocuments /> }
      case 'events':
        return {
          component: <EnrollmentProfileEvents filter={{ studentIds: id ? [+id] : [] }} />,
          subtitle: (
            <Button
              variant="secondary"
              label="Ajouter un évènement"
              icon={PlusIcon}
              onClick={(e) => {
                e.stopPropagation()
                toggleModal('create-event')
              }}
            />
          )
        }
      case 'end-of-stay-appreciations':
        return { component: <EnrollmentProfileEndOfStayAppreciations enrollmentId={data.id} /> }
    }
  }

  const missingLabels = enrollmentMissingDocuments.data
    ? missingDocumentsMapping(enrollmentMissingDocuments.data)
    : []

  const documentsTabIndex = availableTabs.findIndex((t) => t.key === 'documents')

  const isCancelled =
    data?.status === ApplicationStatusEnum.CANCELLED_BEFORE_CONFIRMATION ||
    data?.status === ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION

  if (isPending || enrollmentMissingDocuments.isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  const enrollmentNumber = data.enrollmentNumber ?? data.id.toString()

  return (
    <EnrollmentProvider enrollment={data}>
      <div>
        <UserNavBar
          title={EnrollmentProperties.getEnrollmentName(data)}
          icon={UserTwoIcon}
          badgeTitle={isCancelled ? `Annulé - ${enrollmentNumber}` : enrollmentNumber}
          badgeVariant={isCancelled ? 'danger' : 'grey'}
        />
        <div className={'h-4'} />
        <Box>
          <Button
            type="button"
            label={'Retour'}
            variant={'secondary'}
            icon={ChevronLeftIcon}
            onClick={() => back(Paths.ENROLLMENT_VALIDATED_LIST_PAGE)}
          />
          <div className="h-4" />
          {enrollmentMissingDocuments.data && (
            <>
              <WarningBadge
                text={
                  <div className="flex items-center gap-1">
                    <Typography variant="label-medium">Documents manquants : </Typography>
                    {missingLabels.map((label, index) => (
                      <div className="flex items-center" key={`${label}-${index}`}>
                        <Typography.Link
                          variant="label-medium"
                          color={tailwindColors.red.DEFAULT}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (documentsTabIndex !== -1) handleChange(documentsTabIndex)
                            if (!accordionState.includes('documents'))
                              setAccordionState([...accordionState, 'documents'])
                          }}
                        >
                          {label}
                        </Typography.Link>
                        {index < missingLabels.length - 1 ? ', ' : ''}
                      </div>
                    ))}
                  </div>
                }
              />
              <div className="h-4" />
            </>
          )}
          <div className="flex flex-row justify-between items-start">
            <EnrollmentAdvisorsSelection
              enrollmentId={data.id}
              attributedUser={data.attributedUser}
            />
            <div className="flex  gap-2 items-start">
              <EnrollmentTripHistoryButton enrollmentId={data.id} />
              <EnrollmentParentsRelatedTripsButton enrollmentId={data.id} />
              <EnrollmentDuplicateButton enrollment={data} />
            </div>
          </div>
          <div className="h-2" />
          <EnrollmentStatusOverview enrollment={data} mode="normal" />
          <div className="h-4" />
          {/* a One to One Individuel stay is not eligible to a CSE reduction */}
          {!EnrollmentProperties.isOtoIndividual(data) && (
            <>
              <EnrollmentCseHeader enrollment={data} />
              <div className="h-4" />
            </>
          )}
          <div className="sticky top-2 z-10">
            <CapsuleTab
              variant="secondary"
              activeTab={active}
              onTabChange={handleChange}
              tabs={availableTabs}
            />
          </div>

          <div className="h-4" />
          <div className="flex flex-col gap-4">
            <EnrollmentProfileHeader enrollment={data} onEnrollmentUpdate={refetch} />
            {availableTabs.map((tab) => {
              return (
                <Accordion
                  key={tab.key}
                  variant="primary"
                  id={tab.key}
                  title={tab.label}
                  subtitle={renderAccordion(tab.key).subtitle}
                  open={
                    accordionState.includes(tab.key) ||
                    availableTabs.findIndex((t) => t.key === tab.key) === active
                  }
                  onToggle={() => {
                    if (accordionState.includes(tab.key))
                      setAccordionState(accordionState.filter((key) => key !== tab.key))
                    else setAccordionState([...accordionState, tab.key])
                  }}
                >
                  {renderAccordion(tab.key).component}
                </Accordion>
              )
            })}
          </div>
          <div className="h-4" />
          <EnrollmentComments enrollmentId={Number(id)} />
        </Box>
      </div>

      <Modal
        isOpen={modalState['create-comment']}
        title={'Écrire un commentaire'}
        size="medium"
        hideActions
        icon={MessageIcon}
        onCancel={() => closeModel('create-comment')}
      >
        <EnrollmentCommentForm
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await createEnrollmentCommentMutate({ id: data.id, ...values })
            },
            onCancel: () => closeModel('create-comment')
          })}
        />
      </Modal>

      {/* refunds are an admin action */}
      {userRole === RoleEnum.ADMIN && <EnrollmentOverpaymentRefundModal enrollmentId={data.id} />}

      {data.enrollmentCenter?.center?.id && (
        <Modal
          isOpen={modalState['create-event']}
          title={'Ajouter un évènement'}
          icon={CalendarTwoIcon}
          hasBorderBottom
          hideActions
          size="medium"
          onCancel={() => closeModel('create-event')}
        >
          <>
            <div className="h-4" />
            <Badge
              text={toFullName(data.childFirstName, data.childLastName, {
                options: { mode: 'lastname-firstname' }
              })}
              textVariant="tag"
              textColor={tailwindColors.black.DEFAULT}
              className="mx-4"
            />
            <CenterEventForm
              variant="single-enrollment"
              defaultValues={{ enrollmentIds: [data.id] }}
              centerId={data.enrollmentCenter?.center.id}
              buttonsConfig={modalButtonConfig({
                onConfirm(values) {
                  if (data.enrollmentCenter?.center.id)
                    return mutateAsync({ ...values, centerId: data.enrollmentCenter.center.id })
                },
                onCancel: () => closeModel('create-event')
              })}
            />
          </>
        </Modal>
      )}
    </EnrollmentProvider>
  )
}

export default EnrollmentProfile
